import type { StatisticsSummary } from '@client/app/modules/statistics/interfaces/statistics-summary.interface';
import type { StatisticsVisitor } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import type {
    SiteAnalyticsCellValue,
    SiteAnalyticsLinkCell,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';

export function normalizeVisitorsRows (
    visitors: StatisticsVisitor[],
    buildNameCell?: (visitor: StatisticsVisitor, index: number) => SiteAnalyticsLinkCell | string,
): SiteAnalyticsTableRow[] {
    return visitors.map((visitor, index) => {
        const label = String(visitor.label || visitor.url || '');
        const visits = toInteger(visitor.visits);
        const bounceRateCount = toFloat(visitor.bounce_rate);
        const actionsCount = toFloat(visitor.actions);
        const avgTimeCount = toFloat(visitor.avg_time);

        const bounceRate = visits > 0 ? (bounceRateCount / visits) * 100 : 0;
        const actionsPerVisit = visits > 0 ? actionsCount / visits : 0;
        const avgTime = visits > 0 ? avgTimeCount / visits : 0;

        return {
            id: `${label || 'row'}-${index}`,
            name: buildNameCell ? buildNameCell(visitor, index) : label,
            visits,
            bounceRate: formatPercentage(bounceRate),
            actionsPerVisit: formatDecimal(actionsPerVisit),
            avgTime,
        };
    });
}

export function buildVisitorsColumnAggregates (visitors: StatisticsVisitor[]): Record<string, string | null> {
    const totals = visitors.reduce((accumulator, visitor) => {
        return {
            visits: accumulator.visits + toFloat(visitor.visits),
            bounceRate: accumulator.bounceRate + toFloat(visitor.bounce_rate),
            actions: accumulator.actions + toFloat(visitor.actions),
            avgTime: accumulator.avgTime + toFloat(visitor.avg_time),
        };
    }, {
        visits: 0,
        bounceRate: 0,
        actions: 0,
        avgTime: 0,
    });

    const bounceRateTotal = totals.visits > 0 ? (totals.bounceRate / totals.visits) * 100 : 0;
    const actionsPerVisitTotal = totals.visits > 0 ? totals.actions / totals.visits : 0;
    const avgTimeTotal = totals.visits > 0 ? totals.avgTime / totals.visits : 0;

    return {
        visits: formatInteger(totals.visits),
        bounceRate: formatPercentage(bounceRateTotal),
        actionsPerVisit: formatDecimal(actionsPerVisitTotal),
        avgTime: formatDuration(avgTimeTotal),
    };
}

export function buildVisitorsSummaryColumnAggregates (summary: StatisticsSummary | null): Record<string, string | null> {
    if (!summary) {
        return {
            visits: null,
            bounceRate: null,
            actionsPerVisit: null,
            avgTime: null,
        };
    }

    const visits = toFloat(summary.visits);
    const bounceRateValue = summary.bounce_rate !== null && summary.bounce_rate !== ''
        ? toFloat(summary.bounce_rate)
        : 0;
    const avgTimeValue = summary.avg_time !== null && summary.avg_time !== ''
        ? toFloat(summary.avg_time)
        : 0;
    const avgActionsValue = summary.avg_actions !== null && summary.avg_actions !== ''
        ? toFloat(summary.avg_actions)
        : 0;

    const bounceRate = summary.bounce_rate?.includes('%')
        ? String(summary.bounce_rate).trim()
        : formatPercentage(bounceRateValue);

    const actionsPerVisit = summary.avg_actions !== null && summary.avg_actions !== ''
        ? formatDecimal(avgActionsValue)
        : null;

    return {
        visits: visits > 0 ? formatInteger(visits) : null,
        bounceRate,
        actionsPerVisit,
        avgTime: avgTimeValue > 0 ? formatDuration(avgTimeValue) : null,
    };
}

export function buildLocationColumnAggregates (visitors: StatisticsVisitor[]): Record<string, string | null> {
    if (!Array.isArray(visitors) || visitors.length === 0) {
        return {
            visits: null,
            bounceRate: null,
            actionsPerVisit: null,
            avgTime: null,
        };
    }

    const rowCount = visitors.length;
    const totals = visitors.reduce((accumulator, visitor) => {
        return {
            visits: accumulator.visits + toFloat(visitor.visits),
            bounceRate: accumulator.bounceRate + toFloat(visitor.bounce_rate),
            actions: accumulator.actions + toFloat(visitor.actions),
            avgTime: accumulator.avgTime + toFloat(visitor.avg_time),
        };
    }, {
        visits: 0,
        bounceRate: 0,
        actions: 0,
        avgTime: 0,
    });

    const bounceRateRatio = totals.bounceRate / rowCount / 100;
    const actionsPerVisitTotal = totals.visits > 0 ? totals.actions / totals.visits : 0;
    const avgTimeTotal = totals.avgTime / rowCount;

    return {
        visits: formatInteger(totals.visits),
        bounceRate: formatRatioAsPercentage(bounceRateRatio),
        actionsPerVisit: formatDecimalWithPrecision(actionsPerVisitTotal, 2),
        avgTime: formatDuration(avgTimeTotal),
    };
}

export function buildLocationParentColumnAggregates (
    parent: StatisticsSummary | Record<string, unknown> | null,
): Record<string, string | null> {
    if (!parent) {
        return {
            visits: null,
            bounceRate: null,
            actionsPerVisit: null,
            avgTime: null,
        };
    }

    const parentRecord = parent as Record<string, unknown>;
    const visits = toFloat(parentRecord.visits);
    const bounceRateRaw = toFloat(parentRecord.bounce_rate);
    const actionsRaw = toFloat(parentRecord.actions);
    const avgTimeRaw = toFloat(parentRecord.avg_time);

    const bounceRateRatio = visits > 0 ? bounceRateRaw / visits / 100 : 0;
    const actionsPerVisitTotal = visits > 0 ? actionsRaw / visits : 0;
    const avgTimeTotal = visits > 0 ? avgTimeRaw / visits : 0;

    return {
        visits: visits > 0 ? formatInteger(visits) : null,
        bounceRate: formatRatioAsPercentage(bounceRateRatio),
        actionsPerVisit: formatDecimalWithPrecision(actionsPerVisitTotal, 2),
        avgTime: formatDuration(avgTimeTotal),
    };
}

export function getNameLabel (row: SiteAnalyticsTableRow): string {
    const nameCell = toLinkCell(row.name);
    if (nameCell) {
        return nameCell.label;
    }

    return typeof row.name === 'string' ? row.name : String(row.name ?? '');
}

export function getTotal (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'total' in collection) {
        const total = Number((collection as { total?: unknown }).total);
        if (!Number.isNaN(total) && total > 0) {
            return total;
        }
    }

    return fallback;
}

export function getPerPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'perPage' in collection) {
        const perPage = Number((collection as { perPage?: unknown }).perPage);
        if (!Number.isNaN(perPage) && perPage > 0) {
            return perPage;
        }
    }

    return fallback;
}

export function getCurrentPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'currentPage' in collection) {
        const currentPage = Number((collection as { currentPage?: unknown }).currentPage);
        if (!Number.isNaN(currentPage) && currentPage > 0) {
            return currentPage;
        }
    }

    return fallback;
}

function toLinkCell (value: SiteAnalyticsCellValue): SiteAnalyticsLinkCell | null {
    if (!value || typeof value !== 'object' || !('label' in value) || typeof value.label !== 'string') {
        return null;
    }

    if ('type' in value && value.type === 'state' && 'state' in value && typeof value.state === 'string') {
        return {
            type: 'state',
            label: value.label,
            state: value.state,
        };
    }

    if ('href' in value && typeof value.href === 'string') {
        return {
            type: 'external',
            label: value.label,
            href: value.href,
        };
    }

    return null;
}

function toInteger (value: unknown): number {
    return Math.round(toFloat(value));
}

function toFloat (value: unknown): number {
    const parsed = Number.parseFloat(String(value ?? 0));
    return Number.isNaN(parsed) ? 0 : parsed;
}

function formatInteger (value: number): string {
    return Math.round(value).toLocaleString();
}

function formatDecimal (value: number): string {
    return formatDecimalWithPrecision(value, 1);
}

function formatDecimalWithPrecision (value: number, fractionDigits: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    return safeValue.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

function formatPercentage (value: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `${safeValue.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function formatRatioAsPercentage (value: number): string {
    return formatPercentage(value * 100);
}

function formatDuration (seconds: number): string {
    const safeSeconds = Math.max(0, Math.round(Number(seconds || 0)));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const remainingSeconds = Math.floor(safeSeconds % 60);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
}
